import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js'
import { reactPlugin } from './insights'
import { Layout } from './components/shared/Layout'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { DashboardPage } from './pages/DashboardPage'
import { SessionsPage } from './pages/SessionsPage'
import { ContractsPage } from './pages/ContractsPage'
import { SubUsersPage } from './pages/SubUsersPage'
import { AdminPage } from './pages/AdminPage'
import { GuidePage } from './pages/GuidePage'
import { useAuthStore } from './store/authStore'
import { ToastProvider } from './components/shared/Toast'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (!user.is_admin) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'ohje', element: <GuidePage /> },
      { path: 'guide', element: <Navigate to="/ohje" replace /> },
      {
        path: 'dashboard',
        element: <RequireAuth><DashboardPage /></RequireAuth>,
      },
      {
        path: 'sessions',
        element: <RequireAuth><SessionsPage /></RequireAuth>,
      },
      {
        path: 'contracts',
        element: <RequireAuth><ContractsPage /></RequireAuth>,
      },
      {
        path: 'subusers',
        element: <RequireAuth><SubUsersPage /></RequireAuth>,
      },
      {
        path: 'admin',
        element: <RequireAdmin><AdminPage /></RequireAdmin>,
      },
    ],
  },
])

function ErrorFallback() {
  return (
    <div className="p-8 text-center text-red-600">
      <h2 className="text-lg font-bold mb-2">Jotain meni pieleen</h2>
      <button onClick={() => window.location.reload()}
        className="text-brand-500 hover:underline text-sm">
        Lataa sivu uudelleen
      </button>
    </div>
  )
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <AppInsightsErrorBoundary onError={() => <ErrorFallback />} appInsights={reactPlugin}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </AppInsightsErrorBoundary>
    </Sentry.ErrorBoundary>
  )
}
